/// <amd-module name="Wit/DeliveryPlans/Board/Graph/WorkItemDependencyOverlay" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { format } from "VSS/Core/Util/String";
import { DependencyErrorType, WorkItemDependencyData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { CardDimensions, DependencyCardElement, ElementAndDependency } from "Wit/DeliveryPlans/Common/Interfaces";
import * as Resources from "Wit/DeliveryPlans/Resources";
import { Constants, Position } from "./Contracts";
import { Edge, focusableEdgeElementClass } from "./Edge";

export interface IWorkItemDependencyOverlayProps {
    element: DependencyCardElement;
    onDependencyLineClick?: (
        event: React.MouseEvent<SVGPathElement, MouseEvent>,
        dependency: WorkItemDependencyData,
        anchorElement: HTMLElement
    ) => void;
    parentOffsetLeft: number;
    parentOffsetTop: number;
    cardDimensions: IObservableValue<CardDimensions>;
}

export const WorkItemDependencyOverlay: React.FunctionComponent<IWorkItemDependencyOverlayProps> = ({
    element,
    onDependencyLineClick,
    parentOffsetTop,
    parentOffsetLeft,
    cardDimensions
}) => {
    const sourceElement = element.source;
    const [sourceDimensions, setSourceDimensions] = React.useState<CardDimensions>({
        width: sourceElement.offsetWidth,
        height: sourceElement.offsetHeight
    });
    const targetElements = element.items;
    const sourceId = element.workItemId;
    sourceElement.classList.add("raised");

    // order items by horizontal displacement then vertical displacement
    targetElements
        .sort((a: { element: { offsetLeft: number } }, b: { element: { offsetLeft: number } }) => b.element.offsetLeft - a.element.offsetLeft)
        .sort((a: { element: { offsetTop: number } }, b: { element: { offsetTop: number } }) => b.element.offsetTop - a.element.offsetTop);

    // map elements to four lists: above, below, left, or right
    const [above, below, left, right]: { [key: string]: ElementAndDependency }[] = [{}, {}, {}, {}];

    // space edges evenly along left and right sides of source card
    const startDisplacement = (list: any[], i: number) => (i - (list.length - 1) / 2) * (sourceDimensions.height / list.length);

    const onClickEdge = (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData, anchorElement: HTMLElement) => {
        onDependencyLineClick && onDependencyLineClick(e, dependency, sourceElement);
    };
    targetElements.forEach((item: ElementAndDependency) => {
        const ariaLabel = getEdgeAriaLabel(item.dependency, sourceId);
        if (!ariaLabel) {
            return;
        }

        item.element.classList.add("raised");
        const position = getElementPositionRelativeToSource(sourceElement, item.element);
        switch (position) {
            case Position.Above:
                above[ariaLabel] = item;
                break;

            case Position.Below:
                below[ariaLabel] = item;
                break;

            case Position.Left:
                left[ariaLabel] = item;
                break;

            case Position.Right:
                right[ariaLabel] = item;
                break;
        }
    });
    const dimensionsChanged = (dimensions: CardDimensions): void => {
        if (element.items.length > 0) {
            setSourceDimensions({ height: dimensions.height, width: dimensions.width });
        }
    };
    React.useEffect(() => {
        // focus the first edge on mount
        const firstEdge = document.querySelector<HTMLElement>(`.${focusableEdgeElementClass}`);
        if (firstEdge) {
            firstEdge.focus({ preventScroll: true });
        }
        cardDimensions.subscribe(dimensionsChanged);

        return () => {
            sourceElement.classList.remove("raised");
            targetElements.forEach((e: { element: { classList: { remove: (arg0: string) => any } } }) => e.element.classList.remove("raised"));
            cardDimensions.unsubscribe(dimensionsChanged);
        };
    }, []);

    const edges = [
        ...Object.keys(above).map((ariaLabel, index) => {
            return renderAboveEdge(sourceElement, above[ariaLabel], parentOffsetTop, parentOffsetLeft, onClickEdge, ariaLabel);
        }),
        ...Object.keys(below).map((ariaLabel, index) => {
            return renderBelowEdge(sourceElement, below[ariaLabel], parentOffsetTop, parentOffsetLeft, onClickEdge, ariaLabel);
        }),
        ...Object.keys(left).map((ariaLabel, index) => {
            return renderLeftEdge(
                sourceElement,
                left[ariaLabel],
                parentOffsetTop,
                parentOffsetLeft,
                startDisplacement(Object.values(left), index),
                onClickEdge,
                ariaLabel
            );
        }),
        ...Object.keys(right).map((ariaLabel, index) => {
            return renderRightEdge(
                sourceElement,
                right[ariaLabel],
                parentOffsetTop,
                parentOffsetLeft,
                startDisplacement(Object.values(right), index),
                onClickEdge,
                ariaLabel
            );
        })
    ];
    return <React.Fragment>{edges}</React.Fragment>;
};

function getElementPositionRelativeToSource(sourceElement: HTMLElement, targetElement: HTMLElement): Position {
    const [sourceTop, sourceBottom, sourceLeft, sourceRight] = [
        sourceElement.offsetTop,
        sourceElement.offsetTop + sourceElement.offsetHeight,
        sourceElement.offsetLeft,
        sourceElement.offsetLeft + sourceElement.offsetWidth
    ];
    const [targetTop, targetBottom, targetLeft, targetRight] = [
        targetElement.offsetTop,
        targetElement.offsetTop + targetElement.offsetHeight,
        targetElement.offsetLeft,
        targetElement.offsetLeft + targetElement.offsetWidth
    ];

    if (sourceRight < targetLeft) {
        return Position.Right;
    } else if (sourceLeft > targetRight) {
        return Position.Left;
    } else if (sourceTop > targetBottom) {
        return Position.Above;
    } else {
        return Position.Below;
    }
}

function renderRightEdge(
    sourceElement: HTMLElement,
    target: ElementAndDependency,
    parentOffsetTop: number,
    parentOffsetLeft: number,
    sourceVerticalDisplacement: number,
    onClickEdge: (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData, anchorElement: HTMLElement) => void,
    edgeAriaLabel?: string
) {
    const rootItemOffsetRight = sourceElement.offsetLeft - parentOffsetLeft + sourceElement.offsetWidth;
    const rootItemY = sourceElement.offsetTop - parentOffsetTop + sourceElement.offsetHeight / 2 - sourceVerticalDisplacement;

    // determine the end position of the line
    const targetElement = target.element;
    const targetItemOffsetLeft = targetElement.offsetLeft - parentOffsetLeft;
    const targetItemVerticalCenter =
        targetElement.offsetTop === sourceElement.offsetTop ? rootItemY : targetElement.offsetTop - parentOffsetTop + targetElement.clientHeight / 2;
    const isInvalid = isDependencyInvalid(target.dependency.dependencyError);

    const edgeProps = {
        isInvalid,
        key: `from-${rootItemOffsetRight}-${rootItemY}-to-${targetItemOffsetLeft}-${targetItemVerticalCenter}`,
        from: { x: rootItemOffsetRight, y: rootItemY },
        to: { x: targetItemOffsetLeft, y: targetItemVerticalCenter }
    };
    return (
        <Edge
            {...edgeProps}
            isSourcePredecessor={target.dependency.isSourcePredecessor}
            onClick={e => onClickEdge(e, target.dependency, sourceElement)}
            quadraticBezierHorizontalDisplacement={getHorizontalEdgeQuadraticBezierDisplacement(sourceElement, targetElement, parentOffsetLeft)}
            edgeAriaLabel={edgeAriaLabel}
        />
    );
}

function renderLeftEdge(
    sourceElement: HTMLElement,
    target: ElementAndDependency,
    parentOffsetTop: number,
    parentOffsetLeft: number,
    sourceVerticalDisplacement: number,
    onClickEdge: (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData, anchorElement: HTMLElement) => void,
    edgeAriaLabel?: string
) {
    const rootItemOffsetLeft = sourceElement.offsetLeft - parentOffsetLeft;
    const rootItemY = sourceElement.offsetTop - parentOffsetTop + sourceElement.clientHeight / 2 - sourceVerticalDisplacement;

    // determine the end position of the line
    const targetElement = target.element;
    const targetItemOffsetRight = targetElement.offsetLeft - parentOffsetLeft + targetElement.offsetWidth;
    const targetItemVerticalCenter =
        targetElement.offsetTop === sourceElement.offsetTop ? rootItemY : targetElement.offsetTop - parentOffsetTop + targetElement.clientHeight / 2;
    const isInvalid = isDependencyInvalid(target.dependency.dependencyError);

    const edgeProps = {
        isInvalid,
        key: `from-${rootItemOffsetLeft}-${rootItemY}-to-${targetItemOffsetRight}-${targetItemVerticalCenter}`,
        from: { x: rootItemOffsetLeft, y: rootItemY },
        to: { x: targetItemOffsetRight, y: targetItemVerticalCenter }
    };
    return (
        <Edge
            {...edgeProps}
            isSourcePredecessor={target.dependency.isSourcePredecessor}
            onClick={e => onClickEdge(e, target.dependency, sourceElement)}
            quadraticBezierHorizontalDisplacement={getHorizontalEdgeQuadraticBezierDisplacement(targetElement, sourceElement, parentOffsetLeft)}
            edgeAriaLabel={edgeAriaLabel}
        />
    );
}

function renderAboveEdge(
    sourceElement: HTMLElement,
    target: ElementAndDependency,
    parentOffsetTop: number,
    parentOffsetLeft: number,
    onClickEdge: (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData, anchorElement: HTMLElement) => void,
    edgeAriaLabel?: string
) {
    const targetElement = target.element;
    const x = getVerticalEdgeXCoord(sourceElement, targetElement, parentOffsetLeft);

    // vertical offsets
    const rootItemOffsetTop = sourceElement.offsetTop - parentOffsetTop;
    const targetItemOffsetBottom = targetElement.offsetTop + targetElement.clientHeight - parentOffsetTop;
    const isInvalid = isDependencyInvalid(target.dependency.dependencyError);

    const edgeProps = {
        isInvalid,
        key: `from-${x}-${rootItemOffsetTop}-to-${x}-${targetItemOffsetBottom}`,
        from: { x, y: rootItemOffsetTop },
        to: { x, y: targetItemOffsetBottom }
    };
    return (
        <Edge
            {...edgeProps}
            isSourcePredecessor={target.dependency.isSourcePredecessor}
            onClick={e => onClickEdge(e, target.dependency, sourceElement)}
            edgeAriaLabel={edgeAriaLabel}
        />
    );
}

function renderBelowEdge(
    sourceElement: HTMLElement,
    target: ElementAndDependency,
    parentOffsetTop: number,
    parentOffsetLeft: number,
    onClickEdge: (e: React.MouseEvent<SVGPathElement, MouseEvent>, dependency: WorkItemDependencyData, anchorElement: HTMLElement) => void,
    edgeAriaLabel?: string
) {
    const targetElement = target.element;
    const x = getVerticalEdgeXCoord(sourceElement, targetElement, parentOffsetLeft);

    // vertical offsets
    const rootItemOffsetBottom = sourceElement.offsetTop + sourceElement.clientHeight - parentOffsetTop;
    const targetItemOffsetTop = targetElement.offsetTop - parentOffsetTop;
    const isInvalid = isDependencyInvalid(target.dependency.dependencyError);

    const edgeProps = {
        isInvalid,
        key: `from-${x}-${rootItemOffsetBottom}-to-${x}-${targetItemOffsetTop}`,
        from: { x: x, y: rootItemOffsetBottom },
        to: { x: x, y: targetItemOffsetTop }
    };
    return (
        <Edge
            {...edgeProps}
            isSourcePredecessor={target.dependency.isSourcePredecessor}
            onClick={e => onClickEdge(e, target.dependency, sourceElement)}
            edgeAriaLabel={edgeAriaLabel}
        />
    );
}

/**
 * get the midpoint between the two elements' horizontal overlap
 * @param sourceElement
 * @param targetElement
 * @param parentOffsetLeft
 */
function getVerticalEdgeXCoord(sourceElement: HTMLElement, targetElement: HTMLElement, parentOffsetLeft: number) {
    const rootItemOffsetLeft = sourceElement.offsetLeft - parentOffsetLeft;
    const rootItemOffsetRight = rootItemOffsetLeft + sourceElement.clientWidth;
    const targetItemOffsetLeft = targetElement.offsetLeft - parentOffsetLeft;
    const targetItemOffsetRight = targetItemOffsetLeft + targetElement.clientWidth;
    const overlapLeftX = rootItemOffsetLeft > targetItemOffsetLeft ? rootItemOffsetLeft : targetItemOffsetLeft;
    const overlapRightX = rootItemOffsetRight < targetItemOffsetRight ? rootItemOffsetRight : targetItemOffsetRight;
    const overlapWidth = overlapRightX - overlapLeftX;

    // randomly place the line so lines are less likely to overlap
    // TODO: improve upon this by looking back at previously placed elements
    const randomOffsetX = Math.floor(Math.random() * (overlapWidth + 1));
    return overlapLeftX + randomOffsetX;
}

function getHorizontalEdgeQuadraticBezierDisplacement(sourceElement: HTMLElement, targetElement: HTMLElement, parentOffsetLeft: number) {
    const rootItemOffsetLeft = sourceElement.offsetLeft - parentOffsetLeft;
    const rootItemOffsetRight = rootItemOffsetLeft + sourceElement.clientWidth;
    const targetItemOffsetLeft = targetElement.offsetLeft - parentOffsetLeft;
    const horizontalDistance = targetItemOffsetLeft - rootItemOffsetRight;

    // don't want to shift line if source and target elements are close
    if (horizontalDistance < Constants.horizontalDisplacementThreshold) {
        return 0;
    }

    // generate number between -15 and 15
    // TODO: improve upon this by looking back at previously placed elements
    return Constants.horizontalDisplacementRange / 2 - Math.floor(Math.random() * Constants.horizontalDisplacementRange + 1);
}

function getEdgeAriaLabel(dependency: WorkItemDependencyData, sourceId: number) {
    const sourceItem = dependency.source.id === sourceId ? dependency.source : dependency.sourceChain.find(v => v.id === sourceId);
    if (!sourceItem) {
        // should never happen
        return;
    }

    const message = isDependencyInvalid(dependency.dependencyError)
        ? Resources.WorkItemDependencyAnnouncementInvalid
        : Resources.WorkItemDependencyAnnouncementValid;
    const successorName = dependency.isSourcePredecessor ? dependency.target.name : sourceItem.name;
    const predecessorName = dependency.isSourcePredecessor ? sourceItem.name : dependency.target.name;
    return format(message, successorName, predecessorName);
}

function isDependencyInvalid(errorType: DependencyErrorType) {
    return errorType !== DependencyErrorType.None;
}
